/**
 * Admin — Health/Infra (Wave 1b W1b-19)
 *
 * Backed by the real /admin/health/infra + /admin/health/infra/blocks
 * API. Replaces the prior mock-data stub.
 *
 * Surfaces three operator-facing facets:
 *   1. Host info       — CPU / memory / disk / kernel / load / uptime
 *                          read from /proc/* via the host-info service.
 *   2. GPU detail      — best-effort `nvidia-smi`. Empty list on
 *                          CPU-only hosts (supported profile).
 *   3. Outbound block  — paginated read of `outbound_attempts`. Filter
 *                          by reason + date range.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Cpu, Server, Thermometer, ShieldOff, Search, AlertTriangle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { relativeTime, formatUptime } from '@/lib/format';
import { SectionCard } from '@/components/admin';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/health/infra')({
  component: InfraPage,
});

// ─── API types ───────────────────────────────────────────────────────────

// Bug #1111 — the API returns audience-redacted shapes for tenant
// callers (see packages/api/src/routes/v1/admin/health/infra.ts §646):
// when the caller is not a platform sysadmin, host/gpu come back as
// `{ available: false, note: '...' }` with every other field UNDEFINED.
// The pre-fix type declared every field non-optional and the JSX called
// `.toString()` / `.toFixed()` / `.join()` on them unconditionally, so
// tenant admins hit the error boundary with
//   `Cannot read properties of undefined (reading 'toString')`
// the moment they opened the page. The type now mirrors the wire shape
// (every detail field optional/nullable) and the render path treats the
// redacted view as a first-class state alongside loading/error.
interface HostSnapshot {
  available: boolean;
  note?: string;
  hostname?: string;
  os?: string;
  kernel?: string;
  cpuCores?: number;
  cpuModel?: string | null;
  memoryTotalMb?: number | null;
  memoryUsedMb?: number | null;
  memoryFreeMb?: number | null;
  diskTotalGb?: number | null;
  diskUsedGb?: number | null;
  diskFreeGb?: number | null;
  loadAvg?: [number, number, number];
  uptimeSeconds?: number;
  collectedAt?: string;
  notes?: string[];
}

interface GpuRow {
  index: number;
  name: string;
  vramTotalMb: number | null;
  vramUsedMb: number | null;
  utilizationPercent: number | null;
  temperatureC: number | null;
}

interface GpuSummary {
  available: boolean;
  // Bug #1111 — tenant-audience responses send `note` instead of the
  // full detail; `unavailableReason` may be omitted entirely.
  note?: string;
  unavailableReason?: string | null;
  gpus: GpuRow[];
  collectedAt?: string;
}

interface InfraSummary {
  host: HostSnapshot;
  gpu: GpuSummary;
  airGap: { blocksLast24h: number; totalBlocks: number };
}

interface BlockRow {
  id: string;
  destination: string;
  port: number | null;
  protocol: string | null;
  process: string | null;
  reason: string | null;
  blockedAt: string;
}

interface BlocksPage {
  blocks: BlockRow[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

// ─── Page ────────────────────────────────────────────────────────────────

function InfraPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const PAGE_SIZE = 25;

  // Debounce search filter so we don't refetch on every keystroke.
  // 300ms is the project-wide convention from W1b-23 notification log.
  // Using a setTimeout in render is OK because we only call setState
  // when the value actually changes.
  if (search !== debouncedSearch) {
    setTimeout(() => {
      setDebouncedSearch(search);
      setOffset(0);
    }, 300);
  }

  const summaryQuery = useQuery<InfraSummary>({
    queryKey: ['admin', 'health', 'infra'],
    queryFn: () => apiClient.get<InfraSummary>('/admin/health/infra'),
    refetchInterval: 30_000,
  });

  const blocksQuery = useQuery<BlocksPage>({
    queryKey: ['admin', 'health', 'infra', 'blocks', debouncedSearch, offset],
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (debouncedSearch.trim().length > 0) {
        params.set('reason', debouncedSearch.trim());
      }
      return apiClient.get<BlocksPage>(
        `/admin/health/infra/blocks?${params.toString()}`,
      );
    },
  });

  const host = summaryQuery.data?.host;
  const gpuSummary = summaryQuery.data?.gpu;
  const airGap = summaryQuery.data?.airGap;
  const blocks = blocksQuery.data?.blocks ?? [];
  const pagination = blocksQuery.data?.pagination;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="health-infra">
      <div>
        <h1 className="text-xl font-semibold">Infrastructure</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Host hardware, GPU details, and the air-gap outbound block log.
        </p>
      </div>

      {/* Host */}
      <SectionCard title="Host" icon={Server} description="Physical/virtual host running ArgusAI.">
        {summaryQuery.isLoading && (
          <div className="text-xs text-muted-foreground py-4">
            Loading host info…
          </div>
        )}
        {summaryQuery.isError && (
          <div className="text-xs text-red-600 py-4 flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" />
            Failed to load host info.
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => summaryQuery.refetch()}
            >
              Retry
            </Button>
          </div>
        )}
        {/* Bug #1111 — tenant audience responses set host.available=false
            and send a `note` instead of any detail fields. Render the
            redaction notice first, then fall through to the detail
            grid only when the API actually shipped a number for cores.
            Every probe-derived value (cores, uptime, loadAvg, memory,
            disk, notes) can be undefined for valid responses; guard
            each. */}
        {host && !host.available && (
          <div className="text-xs text-muted-foreground p-3 border rounded bg-muted/20" data-testid="health-infra-host-restricted">
            <div className="font-medium text-foreground/80 mb-1">
              Host metrics not available for this view
            </div>
            <div>
              {host.note ??
                'Host hardware metrics are restricted to platform sysadmins. Tenant admins see request latency and per-service uptime via the Overview and Services tabs.'}
            </div>
          </div>
        )}
        {host && host.available && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Field label="Hostname" value={host.hostname ?? '—'} mono />
                <Field label="OS" value={host.os ?? '—'} />
                <Field label="Kernel" value={host.kernel ?? '—'} mono />
                <Field label="CPU" value={host.cpuModel ?? 'Unknown CPU model'} />
                <Field
                  label="Cores"
                  value={
                    typeof host.cpuCores === 'number'
                      ? String(host.cpuCores)
                      : '—'
                  }
                />
                <Field
                  label="Uptime"
                  value={
                    typeof host.uptimeSeconds === 'number'
                      ? formatUptime(host.uptimeSeconds)
                      : '—'
                  }
                />
              </div>
              <div className="space-y-2">
                {host.memoryTotalMb != null && host.memoryUsedMb != null ? (
                  <ResourceCard
                    label="Memory"
                    used={host.memoryUsedMb}
                    total={host.memoryTotalMb}
                    unit="MB"
                    displayDiv={1024}
                    displayUnit="GB"
                  />
                ) : (
                  <UnavailableCard label="Memory" />
                )}
                {host.diskTotalGb != null && host.diskUsedGb != null ? (
                  <ResourceCard
                    label="Disk"
                    used={host.diskUsedGb}
                    total={host.diskTotalGb}
                    unit="GB"
                  />
                ) : (
                  <UnavailableCard label="Disk" />
                )}
                <div className="p-2.5 border rounded">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    Load average
                  </div>
                  <div className="font-mono text-sm tabular-nums mt-0.5">
                    {Array.isArray(host.loadAvg) && host.loadAvg.length === 3
                      ? host.loadAvg.map((n) => n.toFixed(2)).join(', ')
                      : '—'}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    1m / 5m / 15m
                  </div>
                </div>
              </div>
            </div>
            {host.notes && host.notes.length > 0 && (
              <div className="mt-3 p-2 border rounded bg-amber-500/5 border-amber-500/30">
                <div className="text-[10px] uppercase tracking-wide text-amber-600 dark:text-amber-400 font-semibold mb-1">
                  Probe notes
                </div>
                <ul className="text-[11px] text-muted-foreground list-disc list-inside space-y-0.5">
                  {host.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* GPU */}
      <SectionCard
        title="GPU"
        icon={Cpu}
        description="Hardware accelerator status from nvidia-smi."
      >
        {summaryQuery.isLoading && (
          <div className="text-xs text-muted-foreground py-4">
            Loading GPU info…
          </div>
        )}
        {gpuSummary && !gpuSummary.available && (
          <div className="text-xs text-muted-foreground p-3 border rounded bg-muted/20" data-testid="health-infra-gpu-restricted">
            <div className="font-medium text-foreground/80 mb-1">
              {/* Bug #1111 — when the API redacts (tenant audience) it
                  ships a `note` instead of an unavailableReason. Either
                  is acceptable; only fall back to the CPU-only message
                  when both are absent. */}
              {gpuSummary.note
                ? 'GPU detail restricted'
                : 'No GPUs detected'}
            </div>
            <div>
              {gpuSummary.note ??
                gpuSummary.unavailableReason ??
                'This host appears to be CPU-only.'}
            </div>
          </div>
        )}
        {gpuSummary?.available && (
          <div className="space-y-3">
            {gpuSummary.gpus.map((gpu) => (
              <GpuCard key={gpu.index} gpu={gpu} />
            ))}
          </div>
        )}
      </SectionCard>

      {/* Outbound block log */}
      <SectionCard
        title="Outbound Block Log"
        icon={ShieldOff}
        description="Every outbound network attempt is logged and blocked. This list is the proof your air-gap is enforced."
      >
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by reason…"
              className="h-8 text-xs pl-7"
              data-testid="block-search"
            />
          </div>
          {airGap && (
            <>
              <Badge
                variant="outline"
                className="h-6 text-[10px] gap-1.5 border-emerald-500/40 text-emerald-600 dark:text-emerald-400"
              >
                <ShieldOff className="h-3 w-3" />
                {airGap.blocksLast24h} blocked (24h)
              </Badge>
              <Badge
                variant="outline"
                className="h-6 text-[10px] gap-1.5"
                title="Total outbound block log entries on the host"
              >
                {airGap.totalBlocks} total
              </Badge>
            </>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="max-h-[400px] overflow-y-auto">
              <table
                className="w-full"
                style={{ tableLayout: 'fixed' }}
                data-testid="blocks-table"
              >
                <colgroup>
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '40%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b bg-muted/30 sticky top-0">
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                      When
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                      Destination
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                      Port
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                      Process
                    </th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {blocksQuery.isLoading && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-[11px] text-muted-foreground">
                        Loading…
                      </td>
                    </tr>
                  )}
                  {!blocksQuery.isLoading && blocks.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-[11px] text-muted-foreground">
                        No outbound attempts match this filter.
                      </td>
                    </tr>
                  ) : (
                    blocks.map((a) => (
                      <tr key={a.id} className="border-b hover:bg-muted/20">
                        <td className="px-3 py-2 text-xs tabular-nums" title={a.blockedAt}>
                          {relativeTime(a.blockedAt)}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono truncate" title={a.destination}>
                          {a.destination}
                        </td>
                        <td className="px-3 py-2 text-xs tabular-nums">
                          {a.port ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">
                          {a.process ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-[10px] text-muted-foreground truncate" title={a.reason ?? ''}>
                          {a.reason ?? '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {pagination && pagination.total > 0 && (
          <div className="flex items-center justify-between mt-3 text-xs">
            <div className="text-muted-foreground">
              Showing {Math.min(pagination.offset + 1, pagination.total)}–
              {Math.min(pagination.offset + blocks.length, pagination.total)} of{' '}
              {pagination.total}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={!pagination.hasMore}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────

function GpuCard({ gpu }: { gpu: GpuRow }) {
  const vramPct =
    gpu.vramTotalMb && gpu.vramUsedMb
      ? Math.min(100, (gpu.vramUsedMb / gpu.vramTotalMb) * 100)
      : 0;
  const temp = gpu.temperatureC;
  return (
    <div className="border rounded-md p-3 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-semibold">{gpu.name}</span>
        <Badge variant="outline" className="h-4 text-[9px] px-1.5">
          Slot {gpu.index}
        </Badge>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="p-2.5 border rounded">
          <div className="text-[10px] text-muted-foreground">VRAM</div>
          <div className="text-base font-semibold tabular-nums">
            {/* Bug #1111 — `!= null` covers undefined as well as null;
                tenant-redacted responses ship the gpus[] empty so this
                branch never runs for them, but defensive guards keep
                the page from crashing if the API ever ships a partial
                row. */}
            {gpu.vramUsedMb != null && gpu.vramTotalMb != null
              ? `${(gpu.vramUsedMb / 1024).toFixed(1)} / ${(gpu.vramTotalMb / 1024).toFixed(0)} GB`
              : '—'}
          </div>
          <div className="h-1 bg-muted/50 rounded-full overflow-hidden mt-1">
            <div
              className="h-full bg-purple-500"
              style={{ width: `${vramPct}%` }}
            />
          </div>
        </div>
        <div className="p-2.5 border rounded">
          <div className="text-[10px] text-muted-foreground">Utilization</div>
          <div className="text-base font-semibold tabular-nums">
            {gpu.utilizationPercent != null ? `${gpu.utilizationPercent}%` : '—'}
          </div>
          <div className="h-1 bg-muted/50 rounded-full overflow-hidden mt-1">
            <div
              className="h-full bg-emerald-500"
              style={{ width: `${gpu.utilizationPercent ?? 0}%` }}
            />
          </div>
        </div>
        <div className="p-2.5 border rounded">
          <div className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Thermometer className="h-2.5 w-2.5" />
            Temperature
          </div>
          <div
            className={cn(
              'text-base font-semibold tabular-nums',
              temp != null && temp > 80 && 'text-red-600 dark:text-red-400',
              temp != null && temp > 70 && temp <= 80 && 'text-amber-600 dark:text-amber-400',
            )}
          >
            {temp != null ? `${temp}°C` : '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3 text-xs py-1">
      <div className="w-24 shrink-0 text-[10px] text-muted-foreground uppercase tracking-wide pt-0.5">
        {label}
      </div>
      <div className={cn('flex-1 min-w-0 break-words', mono && 'font-mono text-[11px]')}>
        {value}
      </div>
    </div>
  );
}

function ResourceCard({
  label, used, total, unit, displayDiv = 1, displayUnit,
}: {
  label: string;
  used: number;
  total: number;
  unit: string;
  displayDiv?: number;
  displayUnit?: string;
}) {
  const pct = total === 0 ? 0 : (used / total) * 100;
  const color = pct > 90 ? 'bg-red-500' : pct > 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const u = displayUnit ?? unit;
  return (
    <div className="p-2.5 border rounded">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-semibold tabular-nums">
          {`${(used / displayDiv).toFixed(displayDiv > 1 ? 1 : 0)} / ${(total / displayDiv).toFixed(0)} ${u}`}
        </span>
        <span className="text-[10px] text-muted-foreground tabular-nums">
          {pct.toFixed(0)}%
        </span>
      </div>
      <div className="h-1.5 bg-muted/50 rounded-full overflow-hidden">
        <div
          className={cn('h-full transition-all', color)}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

function UnavailableCard({ label }: { label: string }) {
  return (
    <div className="p-2.5 border rounded bg-muted/10">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">
        {label}
      </div>
      <div className="text-xs text-muted-foreground">
        Probe not available on this host.
      </div>
    </div>
  );
}
